import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';

import type { AuthContextValue } from '@/lib/auth';

/**
 * Contexto que el router pone a disposición de `beforeLoad` y `loader`.
 * Se inyecta en `main.tsx` al montar `<RouterProvider>`.
 */
export interface RouterContext {
  auth: AuthContextValue;
  queryClient: QueryClient;
}

// Las devtools sólo se cargan (y sólo se descargan del servidor) en desarrollo.
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((module) => ({
        default: module.TanStackRouterDevtools,
      })),
    );

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        {/* Arriba a la derecha: abajo taparía la barra de navegación. */}
        <RouterDevtools position="top-right" />
      </Suspense>
    </>
  );
}

function NotFound() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-app flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-5xl font-bold text-brand">404</p>
      <h1 className="text-lg font-semibold">Esta página no existe</h1>
      <p className="text-sm text-ink-muted">
        Puede que el enlace esté mal o que la sección aún no esté disponible.
      </p>
      <a
        href="/"
        className="mt-2 inline-flex min-h-11 items-center rounded-xl bg-brand px-4 font-semibold text-brand-ink"
      >
        Ir al inicio
      </a>
    </main>
  );
}
